import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';
import { useOverlayTriggerTooltip } from '@heidi/utils';

/**
 * Single item in the action dropdown
 */
export interface ActionDropdownItem {
  /** Unique identifier for the item */
  id: string;
  /** Label to display for the item */
  label: string;
  /** Icon component to display */
  icon: LucideIcon;
  /** Callback when the item is clicked */
  onClick: () => void;
  /** Whether the item is disabled */
  disabled?: boolean;
}

/**
 * Nudge content shown for logged out users
 */
export interface ActionDropdownNudge {
  /** Title of the nudge */
  title: string;
  /** Description of the nudge */
  description: string;
  /** CTA button label */
  ctaLabel: string;
  /** CTA button callback */
  onCtaClick: () => void;
}

export interface ActionDropdownProps {
  /** The trigger button icon. Sized and coloured by the trigger `Button`, so pass a bare glyph. */
  triggerIcon: React.ReactNode;
  /** Tooltip label for the trigger button */
  triggerLabel: string;
  /** Array of dropdown menu items */
  items: ActionDropdownItem[];
  /** Whether the user is logged out (shows disabled items + nudge) */
  isLoggedOut?: boolean;
  /** Nudge content to show when logged out */
  nudge?: ActionDropdownNudge;
  /** Side of the dropdown content */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment of the dropdown content */
  align?: 'start' | 'center' | 'end';
  /** Additional className for the dropdown content */
  contentClassName?: string;
  /** Whether the trigger button is disabled */
  disabled?: boolean;
  /** Test id for the dropdown */
  'data-testid'?: string;
}

/**
 * Get the transform-origin class for origin-aware animations.
 * The dropdown scales from the trigger button's position.
 */
function getTransformOrigin(
  side: 'top' | 'bottom' | 'left' | 'right',
  align: 'start' | 'center' | 'end'
): string {
  const originMap: Record<string, Record<string, string>> = {
    bottom: {
      start: 'origin-top-left',
      center: 'origin-top',
      end: 'origin-top-right',
    },
    top: {
      start: 'origin-bottom-left',
      center: 'origin-bottom',
      end: 'origin-bottom-right',
    },
    left: {
      start: 'origin-top-right',
      center: 'origin-right',
      end: 'origin-bottom-right',
    },
    right: {
      start: 'origin-top-left',
      center: 'origin-left',
      end: 'origin-bottom-left',
    },
  };
  return originMap[side]?.[align] ?? 'origin-top-right';
}

/**
 * Reusable action dropdown component that can be used for various action buttons
 * in the action row. Supports both authenticated and logged-out states.
 *
 * @example
 * // Basic usage
 * <ActionDropdown
 *   triggerIcon={<ListPlus />}
 *   triggerLabel="Insert into"
 *   items={[
 *     { id: 'add', label: 'Add to a note', icon: ListPlus, onClick: handleAddToNote },
 *     { id: 'replace', label: 'Replace note', icon: Replace, onClick: handleReplaceNote },
 *   ]}
 * />
 *
 * @example
 * // Logged out state with nudge
 * <ActionDropdown
 *   triggerIcon={<ListPlus />}
 *   triggerLabel="Insert into"
 *   items={[...]}
 *   isLoggedOut={true}
 *   nudge={{
 *     title: 'Strengthen your documentation',
 *     description: 'Add evidence to your notes to support every decision.',
 *     ctaLabel: 'Sign up for access',
 *     onCtaClick: handleSignUp,
 *   }}
 * />
 */
export function ActionDropdown({
  triggerIcon,
  triggerLabel,
  items,
  isLoggedOut = false,
  nudge,
  side = 'bottom',
  align = 'end',
  contentClassName,
  disabled = false,
  'data-testid': testId,
}: ActionDropdownProps) {
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();

  return (
    <DropdownMenu>
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="md"
              iconOnly
              // On the Button, not the glyph — colour set on the SVG shadows the
              // ghost variant's `hover:text-text-primary`.
              className="text-text-secondary"
              aria-label={triggerLabel}
              disabled={disabled}
              data-testid={testId}
            >
              {triggerIcon}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{triggerLabel}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        {...contentProps}
        align={align}
        side={side}
        sideOffset={4}
        className={cn(
          'min-w-[240px]',
          // Origin-aware animation: scale from the trigger button
          getTransformOrigin(side, align),
          // Override default animation with smoother scale effect
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'duration-200 ease-out',
          contentClassName
        )}
      >
        {/* Menu Items */}
        <div className="flex flex-col gap-0.5 p-1">
          {items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              data-testid={`action-${item.id}`}
              onClick={isLoggedOut ? undefined : item.onClick}
              disabled={isLoggedOut || item.disabled}
              // Resting colour is inherited from DropdownMenuContent; the glyph
              // strokes with currentColor, so only the disabled tint needs stating.
              className={isLoggedOut ? 'text-text-disabled' : undefined}
            >
              <item.icon />
              <span>{item.label}</span>
            </DropdownMenuItem>
          ))}
        </div>

        {/* Nudge section for logged out users */}
        {isLoggedOut && nudge && (
          <div className="flex flex-col gap-3 bg-surface-accent px-4 py-3">
            <div className="flex flex-col gap-1">
              <TypographyP2Bold className="text-text-primary">
                {nudge.title}
              </TypographyP2Bold>
              <TypographyP2 className="text-text-secondary">
                {nudge.description}
              </TypographyP2>
            </div>
            <Button size="sm" onClick={nudge.onCtaClick}>
              {nudge.ctaLabel}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ActionDropdown;
